<template>
  <div class="min-h-screen bg-gray-50 p-8">
    <!-- Header -->
    <div class="mb-8">
      <h1 class="text-3xl font-bold text-ecomac-primary">Estadísticas de Soporte</h1>
      <p class="text-gray-600 mt-2">Dashboard de métricas OKR y KPIs en tiempo real</p>
      <div class="flex items-center mt-4 space-x-4">
        <div class="flex items-center space-x-2">
          <div class="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
          <span class="text-sm text-gray-600">Actualización automática cada 30s</span>
        </div>
        <div class="text-sm text-gray-500">
          Última actualización: {{ lastUpdateTime }}
        </div>
        <button 
          @click="refreshData" 
          class="btn-secondary text-sm px-3 py-1"
          :disabled="isRefreshing"
        >
          <svg v-if="!isRefreshing" class="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <svg v-else class="w-4 h-4 mr-1 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          {{ isRefreshing ? 'Actualizando...' : 'Actualizar' }}
        </button>
      </div>
    </div>

    <!-- Statistics Dashboard Grid -->
    <div class="stats-dashboard">
      <!-- Row 1: Global OKR Progress Gauges -->
      <section class="row-okr mb-8">
        <h2 class="text-xl font-semibold text-gray-800 mb-4 flex items-center">
          <svg class="w-6 h-6 mr-2 text-ecomac-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          Progreso Global de OKRs
        </h2>
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div 
            v-for="(okr, index) in okrs" 
            :key="index" 
            class="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
          >
            <h3 class="text-lg font-medium text-gray-900 mb-2">{{ okr.area }}</h3>
            <p class="text-sm text-gray-600 mb-4">{{ okr.objective }}</p>
            <div 
              ref="gaugeRefs" 
              :data-okr-id="okr.id"
              class="gauge-container"
              style="height: 200px;"
            ></div>
            <div class="mt-4 flex justify-between text-sm">
              <span class="text-gray-600">Progreso: {{ okr.current }}/{{ okr.target }}</span>
              <span class="font-semibold" :class="getProgressClass(okr.current, okr.target)">
                {{ Math.round((okr.current / okr.target) * 100) }}%
              </span>
            </div>
          </div>
        </div>
      </section>

      <!-- Row 2: Bar Chart Comparing Key Results vs Goals by Area -->
      <section class="row-bar mb-8">
        <div class="bg-white rounded-lg shadow-md p-6">
          <h2 class="text-xl font-semibold text-gray-800 mb-4 flex items-center">
            <svg class="w-6 h-6 mr-2 text-ecomac-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
            </svg>
            Resultados Clave vs Objetivos por Área
          </h2>
          <div ref="barChartRef" class="bar-chart-container" style="height: 400px;"></div>
        </div>
      </section>

      <!-- Row 3: Line Charts for Time-Series KPIs -->
      <section class="row-line mb-8">
        <h2 class="text-xl font-semibold text-gray-800 mb-4 flex items-center">
          <svg class="w-6 h-6 mr-2 text-ecomac-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
          KPIs de Series Temporales
        </h2>
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div class="bg-white rounded-lg shadow-md p-6">
            <h3 class="text-lg font-medium text-gray-900 mb-4">Tiempo de Resolución</h3>
            <div ref="resolutionTimeRef" class="line-chart-container" style="height: 300px;"></div>
          </div>
          <div class="bg-white rounded-lg shadow-md p-6">
            <h3 class="text-lg font-medium text-gray-900 mb-4">Tiempo de Primera Respuesta</h3>
            <div ref="firstResponseRef" class="line-chart-container" style="height: 300px;"></div>
          </div>
        </div>
      </section>

      <!-- Row 4: Donut Charts for Ticket Volume Distribution -->
      <section class="row-donut">
        <h2 class="text-xl font-semibold text-gray-800 mb-4 flex items-center">
          <svg class="w-6 h-6 mr-2 text-ecomac-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
          </svg>
          Distribución de Volumen de Tickets
        </h2>
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div class="bg-white rounded-lg shadow-md p-6">
            <h3 class="text-lg font-medium text-gray-900 mb-4">Por Estado</h3>
            <div ref="statusDistributionRef" class="donut-chart-container" style="height: 300px;"></div>
          </div>
          <div class="bg-white rounded-lg shadow-md p-6">
            <h3 class="text-lg font-medium text-gray-900 mb-4">Por Prioridad</h3>
            <div ref="priorityDistributionRef" class="donut-chart-container" style="height: 300px;"></div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import { echarts } from '@/plugins/echarts'
import { 
  mockYearlyOKRs, 
  mockDailyKPIs, 
  mockMetricsSummary, 
  getKPIsForDateRange,
  calculateMetricTrend 
} from '../mocks/metrics.js'

// Reactive data
const okrs = ref(mockYearlyOKRs)
const isRefreshing = ref(false)
const lastUpdateTime = ref(formatTime(new Date()))
const refreshInterval = ref(null)

// Chart refs
const gaugeRefs = ref([])
const barChartRef = ref(null)
const resolutionTimeRef = ref(null)
const firstResponseRef = ref(null)
const statusDistributionRef = ref(null)
const priorityDistributionRef = ref(null)

// Chart instances
const gaugeCharts = ref([])
const barChart = ref(null)
const resolutionTimeChart = ref(null)
const firstResponseChart = ref(null)
const statusDistributionChart = ref(null)
const priorityDistributionChart = ref(null)

// Helper functions
function formatTime(date) {
  return date.toLocaleTimeString('es-ES', { 
    hour: '2-digit', 
    minute: '2-digit', 
    second: '2-digit' 
  })
}

function getProgressClass(current, target) {
  const percentage = (current / target) * 100
  if (percentage >= 80) return 'text-green-600'
  if (percentage >= 60) return 'text-yellow-600'
  return 'text-red-600'
}

// Data generation functions
function generateGaugeOption(okr) {
  const percentage = Math.round((okr.current / okr.target) * 100)
  
  return {
    series: [{
      type: 'gauge',
      min: 0,
      max: 100,
      splitNumber: 10,
      radius: '90%',
      axisLine: {
        lineStyle: {
          width: 15,
          color: [
            [0.3, '#DC2626'],
            [0.7, '#F59E0B'],
            [1, '#059669']
          ]
        }
      },
      pointer: {
        itemStyle: {
          color: '#0a2a3f'
        }
      },
      axisTick: {
        distance: -15,
        length: 8,
        lineStyle: {
          color: '#fff',
          width: 2
        }
      },
      splitLine: {
        distance: -15,
        length: 15,
        lineStyle: {
          color: '#fff',
          width: 4
        }
      },
      axisLabel: {
        color: '#666',
        distance: 20,
        fontSize: 10
      },
      detail: {
        valueAnimation: true,
        formatter: '{value}%',
        color: '#0a2a3f',
        fontSize: 20,
        fontWeight: 'bold',
        offsetCenter: [0, '50%']
      },
      data: [{
        value: percentage,
        name: okr.area
      }]
    }]
  }
}

function generateBarChartOption() {
  const areas = okrs.value.map(okr => okr.area)
  const currentValues = okrs.value.map(okr => okr.current)
  const targetValues = okrs.value.map(okr => okr.target)
  
  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      }
    },
    legend: {
      data: ['Actual', 'Objetivo']
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: areas,
      axisLabel: {
        fontSize: 12,
        color: '#666'
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        fontSize: 12,
        color: '#666'
      }
    },
    series: [
      {
        name: 'Actual',
        type: 'bar',
        data: currentValues,
        itemStyle: {
          color: '#3182CE',
          borderRadius: [4, 4, 0, 0]
        }
      },
      {
        name: 'Objetivo',
        type: 'bar',
        data: targetValues,
        itemStyle: {
          color: '#0a2a3f',
          borderRadius: [4, 4, 0, 0]
        }
      }
    ]
  }
}

function generateLineChartOption(metricName, data) {
  const last30Days = data.slice(-30)
  const dates = last30Days.map(kpi => kpi.timestamp.toLocaleDateString('es-ES'))
  const values = last30Days.map(kpi => kpi.value)
  
  return {
    tooltip: {
      trigger: 'axis',
      formatter: function(params) {
        const point = params[0]
        return `${point.name}<br/>${point.seriesName}: ${point.value} ${last30Days[0]?.unit || ''}`
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: dates,
      axisLabel: {
        fontSize: 10,
        color: '#666',
        rotate: 45
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        fontSize: 12,
        color: '#666'
      }
    },
    series: [{
      name: metricName,
      type: 'line',
      data: values,
      smooth: true,
      lineStyle: {
        color: '#059669',
        width: 3
      },
      itemStyle: {
        color: '#059669'
      },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [{
            offset: 0,
            color: 'rgba(5, 150, 105, 0.3)'
          }, {
            offset: 1,
            color: 'rgba(5, 150, 105, 0.05)'
          }]
        }
      }
    }]
  }
}

function generateDonutChartOption(title, data) {
  return {
    tooltip: {
      trigger: 'item',
      formatter: '{b}: {c} ({d}%)'
    },
    legend: {
      orient: 'vertical',
      left: 'left',
      textStyle: {
        fontSize: 12
      }
    },
    series: [{
      type: 'pie',
      radius: ['40%', '70%'],
      avoidLabelOverlap: false,
      itemStyle: {
        borderRadius: 8,
        borderColor: '#fff',
        borderWidth: 2
      },
      label: {
        show: false,
        position: 'center'
      },
      emphasis: {
        label: {
          show: true,
          fontSize: 16,
          fontWeight: 'bold'
        }
      },
      labelLine: {
        show: false
      },
      data: data
    }]
  }
}

// Chart initialization functions
function initializeGauges() {
  nextTick(() => {
    gaugeRefs.value.forEach((ref, index) => {
      if (ref && okrs.value[index]) {
        const chart = echarts.init(ref, 'ecomac')
        const option = generateGaugeOption(okrs.value[index])
        chart.setOption(option)
        gaugeCharts.value[index] = chart
      }
    })
  })
}

function initializeBarChart() {
  nextTick(() => {
    if (barChartRef.value) {
      barChart.value = echarts.init(barChartRef.value, 'ecomac')
      const option = generateBarChartOption()
      barChart.value.setOption(option)
    }
  })
}

function initializeLineCharts() {
  nextTick(() => {
    // Resolution Time Chart
    if (resolutionTimeRef.value) {
      const resolutionData = mockDailyKPIs.filter(kpi => kpi.name === 'Resolution Time')
      resolutionTimeChart.value = echarts.init(resolutionTimeRef.value, 'ecomac')
      const option = generateLineChartOption('Tiempo de Resolución', resolutionData)
      resolutionTimeChart.value.setOption(option)
    }
    
    // First Response Time Chart
    if (firstResponseRef.value) {
      const firstResponseData = mockDailyKPIs.filter(kpi => kpi.name === 'First Response Time')
      firstResponseChart.value = echarts.init(firstResponseRef.value, 'ecomac')
      const option = generateLineChartOption('Tiempo de Primera Respuesta', firstResponseData)
      firstResponseChart.value.setOption(option)
    }
  })
}

function initializeDonutCharts() {
  nextTick(() => {
    // Status Distribution Chart
    if (statusDistributionRef.value) {
      const statusData = [
        { value: 35, name: 'Abierto', itemStyle: { color: '#DC2626' } },
        { value: 25, name: 'En Progreso', itemStyle: { color: '#F59E0B' } },
        { value: 30, name: 'Resuelto', itemStyle: { color: '#059669' } },
        { value: 10, name: 'Cerrado', itemStyle: { color: '#6B7280' } }
      ]
      statusDistributionChart.value = echarts.init(statusDistributionRef.value, 'ecomac')
      const option = generateDonutChartOption('Por Estado', statusData)
      statusDistributionChart.value.setOption(option)
    }
    
    // Priority Distribution Chart
    if (priorityDistributionRef.value) {
      const priorityData = [
        { value: 20, name: 'Alta', itemStyle: { color: '#DC2626' } },
        { value: 45, name: 'Media', itemStyle: { color: '#F59E0B' } },
        { value: 35, name: 'Baja', itemStyle: { color: '#059669' } }
      ]
      priorityDistributionChart.value = echarts.init(priorityDistributionRef.value, 'ecomac')
      const option = generateDonutChartOption('Por Prioridad', priorityData)
      priorityDistributionChart.value.setOption(option)
    }
  })
}

// Resize handler
function handleResize() {
  gaugeCharts.value.forEach(chart => chart && chart.resize())
  barChart.value && barChart.value.resize()
  resolutionTimeChart.value && resolutionTimeChart.value.resize()
  firstResponseChart.value && firstResponseChart.value.resize()
  statusDistributionChart.value && statusDistributionChart.value.resize()
  priorityDistributionChart.value && priorityDistributionChart.value.resize()
}

// Refresh data function
async function refreshData() {
  isRefreshing.value = true
  
  try {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    // Refresh OKRs with slight variations
    okrs.value = mockYearlyOKRs.map(okr => ({
      ...okr,
      current: Math.max(0, okr.current + Math.floor(Math.random() * 6) - 3) // ±3 variation
    }))
    
    // Update all charts
    updateAllCharts()
    
    lastUpdateTime.value = formatTime(new Date())
  } catch (error) {
    console.error('Error refreshing data:', error)
  } finally {
    isRefreshing.value = false
  }
}

function updateAllCharts() {
  // Update gauges
  gaugeCharts.value.forEach((chart, index) => {
    if (chart && okrs.value[index]) {
      const option = generateGaugeOption(okrs.value[index])
      chart.setOption(option)
    }
  })
  
  // Update bar chart
  if (barChart.value) {
    const option = generateBarChartOption()
    barChart.value.setOption(option)
  }
  
  // Line charts and donut charts would also be updated here if data changes
}

// Initialize all charts
function initializeAllCharts() {
  initializeGauges()
  initializeBarChart()
  initializeLineCharts()
  initializeDonutCharts()
}

// Lifecycle hooks
onMounted(() => {
  initializeAllCharts()
  
  // Set up auto-refresh
  refreshInterval.value = setInterval(refreshData, 30000)
  
  // Add resize listener
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  // Clear interval
  if (refreshInterval.value) {
    clearInterval(refreshInterval.value)
  }
  
  // Remove resize listener
  window.removeEventListener('resize', handleResize)
  
  // Dispose charts
  gaugeCharts.value.forEach(chart => chart && chart.dispose())
  barChart.value && barChart.value.dispose()
  resolutionTimeChart.value && resolutionTimeChart.value.dispose()
  firstResponseChart.value && firstResponseChart.value.dispose()
  statusDistributionChart.value && statusDistributionChart.value.dispose()
  priorityDistributionChart.value && priorityDistributionChart.value.dispose()
})
</script>

<style scoped>
.stats-dashboard {
  @apply space-y-8;
}

.gauge-container,
.bar-chart-container,
.line-chart-container,
.donut-chart-container {
  @apply w-full rounded-lg;
}

.btn-secondary {
  @apply bg-gray-200 hover:bg-gray-300 text-gray-700 border border-gray-300 rounded-lg px-4 py-2 transition-colors duration-200 flex items-center;
}

.btn-secondary:disabled {
  @apply opacity-50 cursor-not-allowed;
}

.text-ecomac-primary {
  @apply text-blue-900;
}

/* Responsive adjustments */
.stats-dashboard {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
}

@media (min-width: 768px) {
  .row-okr .grid,
  .row-line .grid,
  .row-donut .grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1024px) {
  .row-okr .grid {
    grid-template-columns: repeat(3, 1fr);
  }
  .row-line .grid,
  .row-donut .grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1280px) {
  .stats-dashboard {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (max-width: 640px) {
  .p-8 {
    @apply p-4;
  }
  
  .text-3xl {
    @apply text-2xl;
  }
  
  .gap-6 {
    @apply gap-4;
  }
  
  .mb-8 {
    @apply mb-6;
  }
}

/* Animation classes are handled by Tailwind directly */

/* Chart container hover effects */
.hover\:shadow-lg:hover {
  @apply shadow-lg;
}

.transition-shadow {
  transition: box-shadow 0.3s ease;
}

/* Loading state styles */
.chart-loading {
  @apply flex items-center justify-center bg-gray-50 rounded-lg;
}

.chart-loading::before {
  content: "Cargando...";
  @apply text-gray-500 text-sm;
}
</style>

